/**
 * All user-facing formatting.
 *
 * Nothing in the app formats a date or number by hand: everything routes through
 * here so introducing a second locale is a change in one file, and so the tenant's
 * locale and timezone are honoured consistently.
 *
 * Timestamps transport as RFC 3339 UTC and are rendered in the tenant's zone.
 */

let locale = 'en-IN'
let timeZone: string | undefined

/** Called by TenantProvider once bootstrap resolves. */
export function configureFormatting(next: { locale?: string; timeZone?: string }): void {
  if (next.locale) locale = next.locale
  if (next.timeZone) timeZone = next.timeZone
}

export function getLocale(): string {
  return locale
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const cache = new Map<string, Intl.DateTimeFormat>()

function formatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${timeZone ?? ''}|${JSON.stringify(options)}`
  let instance = cache.get(key)
  if (!instance) {
    instance = new Intl.DateTimeFormat(locale, timeZone ? { ...options, timeZone } : options)
    cache.set(key, instance)
  }
  return instance
}

/** 15 Sep 2026 */
export function formatDate(value: string | Date | null | undefined, fallback = '—'): string {
  const date = toDate(value)
  if (!date) return fallback
  return formatter({ day: '2-digit', month: 'short', year: 'numeric' }).format(date)
}

/** 15 Sep 2026, 14:32 */
export function formatDateTime(value: string | Date | null | undefined, fallback = '—'): string {
  const date = toDate(value)
  if (!date) return fallback
  return formatter({
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
}

/** 14:32 */
export function formatTime(value: string | Date | null | undefined, fallback = '—'): string {
  const date = toDate(value)
  if (!date) return fallback
  return formatter({ hour: '2-digit', minute: '2-digit', hour12: false }).format(date)
}

/**
 * "3 hours ago", "in 2 days". Uses Intl.RelativeTimeFormat so it localises,
 * unlike a hand-rolled "x ago" helper.
 */
const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['week', 604_800],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
  ['second', 1],
]

export function formatRelative(value: string | Date | null | undefined, fallback = '—'): string {
  const date = toDate(value)
  if (!date) return fallback
  const seconds = (date.getTime() - Date.now()) / 1000
  const absolute = Math.abs(seconds)
  if (absolute < 45) return 'just now'

  const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  for (const [unit, secondsInUnit] of RELATIVE_UNITS) {
    if (absolute >= secondsInUnit || unit === 'second') {
      return relative.format(Math.round(seconds / secondsInUnit), unit)
    }
  }
  return fallback
}

/** "1h 48m" — compact duration for table cells. */
export function formatDuration(minutes: number | null | undefined, fallback = '—'): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return fallback
  const total = Math.max(0, Math.round(minutes))
  if (total < 60) return `${total}m`
  const hours = Math.floor(total / 60)
  const rest = total % 60
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`
}

/** "3 days", "14 hours" — for the idle column. */
export function formatIdle(hours: number | null | undefined, fallback = '—'): string {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) return fallback
  if (hours < 1) return 'under an hour'
  if (hours < 48) return `${Math.round(hours)}h`
  return `${Math.round(hours / 24)}d`
}

export function formatNumber(value: number | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback
  return new Intl.NumberFormat(locale).format(value)
}

export function formatPercent(value: number | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return fallback
  return `${Math.round(value)}%`
}

export function formatBytes(bytes: number | null | undefined, fallback = '—'): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return fallback
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`
}

/** "+91 96566 60656" — grouped for readability, never reformatted for transport. */
export function formatPhone(phone: string | null | undefined, fallback = '—'): string {
  if (!phone) return fallback
  const match = /^(\+\d{1,3})(\d{5})(\d{5})$/.exec(phone.replace(/\s/g, ''))
  return match ? `${match[1]} ${match[2]} ${match[3]}` : phone
}

/** Unit label used in tables: "B-3805 · ASBL Spectra". */
export function formatUnitLabel(unit: {
  number?: string | null
  label?: string | null
  project_name?: string | null
}): string {
  const parts = [unit.number, unit.project_name].filter(Boolean)
  if (parts.length) return parts.join(' · ')
  return unit.label ?? '—'
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('') || '?'
}

/** Sentence-case a snake_case backend token when no explicit label exists. */
export function humanise(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback
  const spaced = value.replace(/[_.]/g, ' ').trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
